/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from '@vlinder-web/plugin-agent-service-react';
import { userListConfig } from '../../config/submittedApp.config';
import { StorageModule } from '@vlinder-web/storage-module-react';
import moment from 'moment-timezone';
import { toHeaderCase } from 'js-convert-case';
import { GET_SUBMITTED_APPLICATION } from '@pages/SubmittedApplications/api/graphql';
import { Graphql } from '@common/graphql';
import { vappContainer } from '@/application';
import { apiConfig } from '@/config/apiConfig';
import { AppNavigatorService } from '@/AppNavigator/services';
import { APPLICATION_TYPE } from '@/config/applicationType.config';
import { APPLICATION_STATUS } from '@/config/applicationStatus.config';

const loginSrv = () => {
  return vappContainer.get<AppNavigatorService>(
    AppNavigatorService.displayName
  );
};
export interface ISubmittedAppMethods extends IPluginMethodMap {
  getSubmittedApp(args: any): Promise<any>;
}

export class SubmittedAppService implements IAgentPlugin {
  readonly methods: ISubmittedAppMethods;

  constructor() {
    this.methods = {
      getSubmittedApp: this.getSubmittedApp.bind(this),
    };
  }

  private async getSubmittedApp(args: any) {
    const storageService = StorageModule.getInstance();
    const _localData = storageService.get('userProfile', 'local');
    const _sessionData = storageService.get('userProfile', 'session');
    const userProfile = storageService.get('myProfile', 'session');
    const role = userProfile?.org?.orgRole;
    const userData = _sessionData ?? _localData;
    try {
      let gql = Graphql.withBaseUrl(apiConfig.gql.url);
      const token = loginSrv()?.getToken();
      console.log(`getUserListsProps service token`, token);
      if (token) {
        gql = gql.withHeaders({
          authentication: token,
        });
      }
      const client = gql.getClient();
      const statusIn = [
        'PROCESSING_FEES_RECEIVED',
        'SUBMITTED',
        'PROCESSING_OFFICER_REVIEW',
        'PROCESSING_OFFICER_SUPERVISOR_REVIEW',
        'MINISTRY_REVIEW',
        'DIRECTOR_REVIEW',
        'DIRECTOR_REVIEW_INVESTIGATION',
        'COMMITEE_REVIEW_INVESTIGATION',
        'COMMITEE_REVIEW',
        'MINISTER_REVIEW_INVESTIGATION',
        'MINISTER_REVIEW',
        'COMPLIANCE_ISSUE_DIRECTOR_REVIEW',
        'WP_FEES_RECEIVED',
        'COMPLIANCE_CHECK_AND_SCHEDULE_DATE',
        'COMPLIANCE_CHECK',
        'PICA_SCHEDULE_DATE',
        'RESUBMITTED',
        'APPOINTMENT_SCHEDULED',
        'PICA_APPOINTMENT_SCHEDULED',
        'PROCESSING_OFFICER_REVIEW_QUALIFICATIONS',
        'PROCESSING_OFFICER_REVIEW_ORIGINAL_DOCUMENTS',
        'PROCESSING_OFFICER_GENERATE_CERTIFICATE',
        'MINISTER_SIGN_CERTIFICATE',
        'PROCESSING_OFFICER_APPLY_SEAL',
        'READY_FOR_PICKUP',
        'UNDER_INVESTIGATION',
        'NOT_QUALIFIED',
        'PICA_VERIFY_LANDING_STATUS_AND_INTERPOL_CHECK',
        'PROCESSING_OFFCIER_VERIFY_CERTIFICATE_AUTHENTICITY',
        'MINISTER_SIGN_APPROVAL_LETTER',
        'DISTRIBUTE_LETTER',
        'PROCESSED',
        'DRAFT_LETTER_AND_SCHEDULE_APPOINTMENT',
        'DIRECTOR_REVIEW_DENIAL',
        'MEETING_WITH_INVESTIGATING_OFFICER',
        'PICA_STAMP_PASSPORT',
        'MLSS_APPOINTMENT_SCHEDULED',
        'PROCESSING_OFFICER_REVIEW_INVESTIGATION',
      ];
      const roetable = [
        {
          column: 'type',
          op: 'In',
          value: ['CARICOM_RENEW_WORK_PERMIT', 'CARICOM_NEW_WORK_PERMIT'],
        },
      ];
      if (client) {
        const res = await client.query(GET_SUBMITTED_APPLICATION, {
          input: {
            skip: args?.offset,
            limit: args?.size,
            where: [
              ...(args?.where ?? []),
              ...(role === 'CARICOM_BUSSINESS_ORG' ? roetable : []),
              {
                column: 'status',
                op: 'In',
                value: statusIn,
              },
            ],
            order: [
              {
                column: 'updatedAt',
                direction: 'DESC',
              },
            ],
          },
        });
        if (res?.data) {
          console.log(`table data`, res);
          let _rowData = this.formatUserTable(
            res?.data?.getListOfApplication?.data
          );
          console.log(`row data`, _rowData);
          return {
            ...userListConfig,
            ultUsers: _rowData,
            userRoles: res?.data?.getRoleList?.data,
            isEmpty: _rowData
              ? res?.data?.getListOfApplication?.data?.length <= 0
              : true,
            // studentGroup: holderGroups,
            activePage: res?.data?.getListOfApplication?.currentPage,
            pageSize: res?.data?.getListOfApplication?.pageSize,
            totalCount: res?.data?.getListOfApplication?.totalCount,
            totalPages: res?.data?.getListOfApplication?.totalPages,
          };
        }
      }
    } catch (err: any) {
      console.log(`error in table data`, err);
      throw err;
    }
  }

  private formatUserTable(data: any) {
    let _rowData: any =
      Array.isArray(data) &&
      data.map((item: any, index: number) => {
        return {
          id: item?.applicationNo,
          // status: item?.status,
          name: item?.applicantInfo?.firstName
            ? `${
                item?.applicantInfo?.lastName
                  ? toHeaderCase(item?.applicantInfo?.lastName ?? '')
                  : ''
              }, ${toHeaderCase(item?.applicantInfo?.firstName)} `
            : // ${
              //     item?.applicantInfo?.middleName
              //       ? toHeaderCase(item?.applicantInfo?.middleName ?? '')
              //       : ''
              //   }`
              '',
          type: this.getFormattedType(item?.type),

          appliedOn: this.formatCreatedDate(item?.appliedOn),
          status: this.getFormattedStatus(item?.displayStatus),
          applicationId: item?.applicationNo,
          nationality: item?.applicantInfo?.nationality ?? '-',
        };
      });

    return _rowData;
  }

  private formatCreatedDate(date: Date) {
    if (date) {
      return moment(date).format('LLL');
    }
  }

  private getFormattedType(type: string) {
    if (APPLICATION_TYPE?.[type]) {
      return APPLICATION_TYPE?.[type];
    }
    return '-';
  }
  private getFormattedStatus(status: string) {
    if (APPLICATION_STATUS?.[status]) {
      return APPLICATION_STATUS?.[status];
    }
    return '-';
  }
}
